import { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { useCreateObjective, useUpdateObjective } from "@/hooks/useObjectives";
import { Objective, ObjectiveStatus } from "@/types";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { UserCombobox } from "@/components/UserCombobox";
import { Loader2 } from "lucide-react";
import { CATEGORY_LABEL, OBJECTIVE_CATEGORIES, OBJECTIVE_STATUSES, STATUS_CONFIG } from "./okrConstants";
import { objectiveSchema, ObjectiveFormValues } from "./objective-form-schema";
import { CONTEXT_TEMPLATE, ContextMarkdownField, contextPatchForSubmit } from "./context-markdown-field";
import { okrErrorDescription } from "@/lib/okr-errors";

interface ObjectiveFormProps {
  initialData?: Partial<Objective>;
  onSuccess?: (objective: Objective) => void;
  hideButtons?: boolean;
  onSubmitRef?: (submitFn: () => void) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

export function ObjectiveForm({
  initialData,
  onSuccess,
  hideButtons = false,
  onSubmitRef,
  onSubmittingChange,
}: ObjectiveFormProps) {
  const { toast } = useToast();
  const isEditing = !!initialData?.pid;
  const createMutation = useCreateObjective();
  const updateMutation = useUpdateObjective();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ObjectiveFormValues>({
    resolver: zodResolver(objectiveSchema),
    defaultValues: {
      name: initialData?.name || "",
      category: initialData?.category,
      description: initialData?.description || "",
      // Creating: seed the why/how template. Editing: show what is stored, template only if empty.
      context: initialData?.pid ? (initialData.context ?? "") : CONTEXT_TEMPLATE,
      startDate: initialData?.startDate ? initialData.startDate.split("T")[0] : "",
      dueDate: initialData?.dueDate ? initialData.dueDate.split("T")[0] : "",
      ownerUserPid: initialData?.owner?.pid || "",
      status: initialData?.pid ? (initialData.status ?? ObjectiveStatus.ACTIVE) : undefined,
    },
  });

  const onSubmit = useCallback(async (values: ObjectiveFormValues) => {
    setIsSubmitting(true);
    onSubmittingChange?.(true);
    try {
      const payload = {
        name: values.name,
        category: values.category,
        description: values.description || undefined,
        // Blanking a stored strategy while editing is an explicit clear (null), like the owner field.
        context: contextPatchForSubmit(values.context, isEditing && !!initialData?.context),
        startDate: values.startDate,
        dueDate: values.dueDate,
        // Editing an owned objective down to no owner is an explicit clear; the BE reads `null` as that.
        ownerUserPid: values.ownerUserPid || (isEditing && initialData?.owner ? null : undefined),
      };

      let result: Objective;
      if (isEditing && initialData?.pid) {
        result = await updateMutation.mutateAsync({
          pid: initialData.pid,
          data: { ...payload, status: values.status },
        });
        toast({ title: "Objective updated", description: "Changes saved successfully." });
      } else {
        result = await createMutation.mutateAsync(payload);
        toast({ title: "Objective created", description: `"${values.name}" has been created.` });
      }
      onSuccess?.(result);
    } catch (error) {
      toast({ title: "Error", description: okrErrorDescription(error, "Something went wrong. Please try again."), variant: "destructive" });
    } finally {
      setIsSubmitting(false);
      onSubmittingChange?.(false);
    }
  }, [isEditing, initialData?.pid, initialData?.owner, initialData?.context, createMutation, updateMutation, toast, onSuccess, onSubmittingChange]);

  useEffect(() => {
    if (onSubmitRef) {
      onSubmitRef(() => form.handleSubmit(onSubmit)());
    }
  }, [onSubmitRef, form, onSubmit]);

  const inputField = (name: keyof ObjectiveFormValues, label: string, type: string, placeholder?: string) => (
    <FormField control={form.control} name={name} render={({ field }) => (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        <FormControl>
          <Input type={type} placeholder={placeholder} {...(type === "number" ? { min: 0, max: 100 } : {})} {...field} />
        </FormControl>
        <FormMessage />
      </FormItem>
    )} />
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {inputField("name", "Name *", "text", "e.g. Increase revenue by 20%")}

        <div className="grid grid-cols-2 gap-4">
          <FormField control={form.control} name="category" render={({ field }) => (
            <FormItem>
              <FormLabel>Category *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl><SelectTrigger><SelectValue placeholder="Fintech / AI / Company" /></SelectTrigger></FormControl>
                <SelectContent>
                  {OBJECTIVE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{CATEGORY_LABEL[c]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )} />
          {isEditing && (
            <FormField control={form.control} name="status" render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    {OBJECTIVE_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{STATUS_CONFIG[s].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
          )}
        </div>

        <FormField control={form.control} name="description" render={({ field }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl><Textarea placeholder="Optional description..." rows={3} {...field} /></FormControl>
            <FormMessage />
          </FormItem>
        )} />

        <FormField control={form.control} name="context" render={({ field }) => (
          <FormItem>
            <FormLabel>Strategy</FormLabel>
            <FormControl>
              <ContextMarkdownField value={field.value ?? ""} onChange={field.onChange} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} />

        <FormField control={form.control} name="ownerUserPid" render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Owner</FormLabel>
            <FormControl>
              <UserCombobox
                value={field.value}
                onChange={field.onChange}
                initialUser={initialData?.owner ?? null}
                placeholder="Select owner…"
                clearable
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} />

        <div className="grid grid-cols-2 gap-4">
          {inputField("startDate", "Start Date *", "date")}
          {inputField("dueDate", "Due Date *", "date")}
        </div>

        <p className="text-xs text-muted-foreground">Progress is computed from the weighted progress of its Key Results.</p>

        {!hideButtons && (
          <div className="flex justify-end">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? "Update" : "Create"} Objective
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
}
